import { useEffect } from "preact/hooks";
import { BackButton } from "../components/ui/BackButton";
import { ImageWithSkeleton } from "../components/ui/ImageWithSkeleton";
import { EventDetailSkeleton } from "../components/ui/Skeleton";
import { useEdition } from "../hooks/useEdition";
import { useTranslations } from "../hooks/useTranslations";
import { usePlace } from "../hooks/useWidgetQueries";
import { getImageUrl } from "../lib/image";
import { getLinkIcon } from "../lib/linkIcons";
import { getLocalizedName, getLocalizedValue } from "../lib/localization";

type PlaceDetailPageProps = {
  placeId: string;
  onBack: () => void;
};

const MapPinIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export function PlaceDetailPage({ placeId, onBack }: PlaceDetailPageProps) {
  const { editionId, locale, edition } = useEdition();
  const t = useTranslations();
  const { data: place, loading } = usePlace(placeId, editionId);
  const fallback = edition?.default_locale_code;

  const placeName = place
    ? getLocalizedName(place, locale, fallback)
    : null;

  useEffect(() => {
    if (placeName && edition?.festival?.name) {
      document.title = `${placeName} - ${edition.festival.name}`;
    }
  }, [placeName, edition]);

  if (loading) {
    return (
      <div className="festapp-event-detail-page">
        <BackButton onClick={onBack} />
        <EventDetailSkeleton />
      </div>
    );
  }

  if (!place) {
    return (
      <div className="festapp-event-detail-page">
        <BackButton onClick={onBack} />
        <div className="festapp-error">
          <p>{t.placeNotFound}</p>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl(place.image, "medium");
  const description = getLocalizedValue(place, "description", locale, fallback);
  const placeTypeName = place.place_type
    ? getLocalizedName(place.place_type, locale, fallback)
    : null;

  return (
    <div className="festapp-event-detail-page">
      <BackButton onClick={onBack} />

      <div className="festapp-event-detail">
        {/* Hero */}
        <div className="festapp-event-hero">
          <div className="festapp-event-hero-image-container">
            <ImageWithSkeleton
              src={imageUrl}
              alt={placeName ?? ""}
              className="festapp-event-hero-image"
              fallbackIcon={MapPinIcon}
            />
          </div>
          <h1 className="festapp-event-hero-name">{placeName}</h1>
          {placeTypeName && (
            <p className="festapp-event-hero-categories">{placeTypeName}</p>
          )}
        </div>

        {/* Description (rich text HTML) */}
        {description && (
          <section className="festapp-event-section">
            <div
              className="festapp-place-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </section>
        )}

        {/* Links */}
        {place.links && place.links.length > 0 && (
          <section className="festapp-event-section">
            <div className="festapp-artist-links">
              {place.links.map((link) => {
                const { Icon, label } = getLinkIcon(link.url);
                return (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="festapp-artist-link"
                    title={label}
                  >
                    <Icon className="festapp-artist-link-icon" />
                  </a>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
